/**
 * The letterhead every printed report shares — the client report, the
 * timesheet (invoice attachment) and the expense package — so a client sees
 * one firm's paperwork, not three apps'.
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ [logo]  or  Company Name             name · address · email  │  brand row
 *   ├══════════════════════════════════════════════════════════════┤  2px rule
 *   │ KIND OF DOCUMENT                                  Label value │
 *   │ Title                                             Label value │  title block
 *   │ subtitle                                                      │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * The logo always sits top-left. With no logo, the company name takes its
 * place in the same spot; with a logo, the name moves to the contact lines on
 * the right, so it appears exactly once either way. Set both in Settings →
 * Business.
 */

import type { CSSProperties, ReactNode } from "react";

import { useBusiness } from "@/lib/business";

export function ReportMasthead({ kind, title, subtitle, meta = [], showBrand = true }: {
  /** What the document is: "Expense reimbursement", "Timesheet", "Executive report". */
  kind: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-hand facts: date, period, invoice number. Empty values are skipped. */
  meta?: [string, ReactNode | null | undefined | false][];
  /** The timesheet lets you print without your own details. */
  showBrand?: boolean;
}) {
  const [business] = useBusiness();
  const contact = [
    business.logo ? business.name : "",
    business.address, business.email,
    business.taxId && `Tax ID: ${business.taxId}`,
  ].filter(Boolean);
  const facts = meta.filter(([, v]) => v !== undefined && v !== null && v !== false && v !== "");
  const brandless = showBrand && !business.logo && !business.name;

  return (
    <header style={{ marginBottom: 22 }}>
      {showBrand && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20,
          minHeight: 44, paddingBottom: 12, borderBottom: "2px solid var(--ink)", flexWrap: "wrap",
        }}>
          <div style={{ minWidth: 0 }}>
            {business.logo ? (
              <img src={business.logo} alt={business.name || "Logo"}
                style={{ height: 40, maxWidth: 220, objectFit: "contain", display: "block" }} />
            ) : business.name ? (
              <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: "-0.01em" }}>{business.name}</div>
            ) : (
              <div className="no-print" style={{ fontSize: 12, color: "var(--muted)" }}>
                Add your logo or business name in Settings → Business to brand this report.
              </div>
            )}
          </div>
          {contact.length > 0 && (
            <div style={{ textAlign: "right", fontSize: 11.5, color: "var(--muted)", lineHeight: 1.5 }}>
              {contact.map((c, i) => (
                <div key={i} style={i === 0 && business.logo ? { color: "var(--ink2)", fontWeight: 600, fontSize: 12.5 } : undefined}>{c}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 20, flexWrap: "wrap",
        paddingTop: showBrand && !brandless ? 16 : 0,
        ...(showBrand ? {} : { paddingBottom: 12, borderBottom: "2px solid var(--ink)" }),
      }}>
        <div style={{ minWidth: 0, flex: "1 1 280px" }}>
          <div style={reportLabel}>{kind}</div>
          <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 4 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 13, color: "var(--ink3)", marginTop: 4 }}>{subtitle}</div>}
        </div>
        {facts.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: "3px 14px", fontSize: 12, textAlign: "right", flex: "none" }}>
            {facts.map(([k, v]) => (
              <div key={k} style={{ display: "contents" }}>
                <span style={{ color: "var(--muted)" }}>{k}</span>
                <span style={{ color: "var(--ink)", fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}

/** The business's footer note (payment terms, remittance), as every report ends. */
export function ReportFooter({ children }: { children?: ReactNode }) {
  const [business] = useBusiness();
  if (!children && !business.notes) return null;
  return (
    <div style={{ marginTop: 26, paddingTop: 12, borderTop: "1px solid var(--line)", fontSize: 11.5, color: "var(--muted)", whiteSpace: "pre-wrap" }}>
      {children ?? business.notes}
    </div>
  );
}

// ---- Shared report typography ----------------------------------------------

/** The sheet itself: the same margins on every report. */
export const reportPaper: CSSProperties = { padding: "38px 44px" };
/** Small uppercase labels: the document kind, section headings' eyebrows. */
export const reportLabel: CSSProperties = {
  fontSize: 10.5, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)",
};
export const reportH2: CSSProperties = { margin: "0 0 12px", fontSize: 16, fontWeight: 600 };
export const reportTable: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 12.5 };
export const reportTh: CSSProperties = {
  ...reportLabel, textAlign: "left", padding: "7px 8px", background: "var(--tint)", borderBottom: "1px solid var(--line2)",
};
export const reportTd: CSSProperties = { padding: "7px 8px", borderBottom: "1px solid var(--tint2)", verticalAlign: "top" };
export const reportTotalTd: CSSProperties = { padding: "9px 8px", borderTop: "2px solid var(--line2)", fontWeight: 600 };
export const reportRight: CSSProperties = { textAlign: "right" };
